﻿import { NavBar, Button, Container, Card, Select, MenuItem } from "../components";
import * as _ from "lodash";
import { useForm, Controller, SubmitHandler } from 'react-hook-form';
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useAppActions } from "../hooks";
import { getUserPreferences, setUserPreferences } from "../services/userPreferencesService";
import { CardActions, CardContent, CardHeader, FormHelperText } from "@mui/material";

const schema = z.object({
    slideTransitionAnimationStyle: z.enum(["none", "slide", "flip"], {
        error: "Please select a slide transition animation"
    }),
    voice: z.object({
        languageCode: z.string(),
        name: z.string()
    }, {
        error: "Please select a Narration Voice."
    })
});
type FormValues = z.infer<typeof schema>;

const animationOptions = [{ value: "none", label: "No Animations" }, { value: "slide", label: "Slide In/Out" }, { value: "flip", label: "Page Turn" }];
const voiceOptions = [
    {
        value: {
            languageCode: "en-US",
            name: "en-US-Wavenet-C"
        },
        label: "US - Female 1"
    }, {
        value: {
            languageCode: "en-US",
            name: "en-US-Wavenet-E"
        },
        label: "US - Female 2"
    }, {
        value: {
            languageCode: "en-US",
            name: "en-US-Wavenet-F"
        },
        label: "US - Female 3"
    }, {
        value: {
            languageCode: "en-US",
            name: "en-US-Wavenet-A"
        },
        label: "US - Male 1"
    }, {
        value: {
            languageCode: "en-US",
            name: "en-US-Wavenet-B"
        },
        label: "US - Male 2"
    }, {
        value: {
            languageCode: "en-US",
            name: "en-US-Wavenet-D"
        },
        label: "US - Male 3"
    }, {
        value: {
            languageCode: "en-GB",
            name: "en-GB-Wavenet-A"
        },
        label: "GB - Female 1"
    }, {
        value: {
            languageCode: "en-GB",
            name: "en-GB-Wavenet-C"
        },
        label: "GB - Female 2"
    }, {
        value: {
            languageCode: "en-GB",
            name: "en-GB-Wavenet-B"
        },
        label: "GB - Male 1"
    }, {
        value: {
            languageCode: "en-GB",
            name: "en-GB-Wavenet-D"
        },
        label: "GB - Male 2"
    }, {
        value: {
            languageCode: "en-AU",
            name: "en-AU-Wavenet-A"
        },
        label: "AU - Female 1"
    }, {
        value: {
            languageCode: "en-AU",
            name: "en-AU-Wavenet-C"
        },
        label: "AU - Female 2"
    }, {
        value: {
            languageCode: "en-AU",
            name: "en-AU-Wavenet-B"
        },
        label: "AU - Male 1"
    }, {
        value: {
            languageCode: "en-AU",
            name: "en-AU-Wavenet-D"
        },
        label: "AU - Male 2"
    }
]

function UserPreferences() {
    const { showSuccess } = useAppActions();
    let preferences = {
        ...getUserPreferences()
    };
    preferences.voice = _.find(voiceOptions, vo => vo.value.name === preferences?.voice?.name)?.value;
    const { handleSubmit, formState: { errors }, control } = useForm({ resolver: zodResolver(schema), defaultValues: preferences });

    const onSubmit: SubmitHandler<FormValues> = formModel => {
        setUserPreferences(formModel);
        showSuccess("Your preferences have been saved successfully.");
    };

    return (
        <>
            <NavBar title="User Preferences" />

            <Container className="flex-grow-1 d-flex align-items-center justify-content-center" maxWidth="sm">
                <form onSubmit={handleSubmit(onSubmit)} className="flex-grow-1">
                    <Card>
                        <CardHeader title="User Preferences" />
                        <CardContent className="vertical-form">
                            <div className="vertical-form-field">Slide Transition Animation Style:</div>
                            <Controller
                                name="slideTransitionAnimationStyle"
                                control={control}
                                render={({
                                    field,
                                    fieldState: { error }
                                }) => (
                                    <div className="d-flex flex-column">
                                        <Select
                                            {...field}
                                            variant="outlined"
                                            error={!!error}
                                            fullWidth>
                                            {
                                                animationOptions.map(o => <MenuItem value={o.value}>{o.label}</MenuItem>)
                                            }
                                        </Select>
                                        <FormHelperText error={!!error}>{error?.message}</FormHelperText>
                                    </div>
                                )}
                            />

                            <div className="vertical-form-field">Narration Voice:</div>
                            <Controller
                                name="voice"
                                control={control}
                                render={({
                                    field: { value, onChange },
                                    fieldState: { error }
                                }) => (
                                    <div className="d-flex flex-column">
                                        <Select
                                            value={value.name}
                                            onChange={(event) => onChange(voiceOptions.find(vo => vo.value.name === event.target.value).value)}
                                            variant="outlined"
                                            error={!!error}
                                            fullWidth>
                                            {
                                                voiceOptions.map(o => <MenuItem value={o.value.name}>{o.label}</MenuItem>)
                                            }
                                        </Select>
                                        <FormHelperText error={!!error}>{error?.message}</FormHelperText>
                                    </div>
                                )}
                            />
                        </CardContent>
                        <CardActions>
                            <Button type="submit" variant="contained" color="primary">
                                Save Preferences
                            </Button>
                        </CardActions>
                    </Card>
                </form>
            </Container>
        </>
    );
}

export default UserPreferences;